import useStaticVersionResources from '@/hooks/api/datasets/static/useStaticVersionResources.ts';
import useRetrieveStaticDatasets from '@/hooks/api/datasets/static/useRetrieveStaticDatasets.ts';
import useRetrieveLinkedDatasets from '@/hooks/api/datasets/linked/useRetrieveLinkedDatasets.ts';
import useRetrieveUpstreamDataOutputs from '@/hooks/api/steps/useRetrieveUpstreamDataOutputs.ts';
import useRetrieveEditorLocalSubflowContext from '@/hooks/api/automations/useRetrieveEditorLocalSubflowContext.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import { useMemo } from 'react';
import {
  DataInput,
  DataInputConfig,
  DataInputType,
  DatasetType,
} from '@/types/automationFields.ts';
import { datasetTypeToString } from '@/utils/datasetTypes.ts';
import { reportException } from '@/services/sentry.ts';
import { withIconsForGroups } from '@/context/DataInputIconContext/utils.ts';
import { useDataInputIconContext } from '@/context/DataInputIconContext/useDataInputIconContext.ts';

export const makeStaticOptionValue = (dataset_id: string) => `static ${dataset_id}`;
export const makeLinkedOptionValue = (dataset_id: string) => `linked ${dataset_id}`;
export const makeDynamicOptionValue = (stepId: string, output_id?: string | null) =>
  output_id ? `dynamic ${stepId} ${output_id}` : `dynamic ${stepId}`;
export const makeEmptyOptionValue = (datasetType: DatasetType) => `empty ${datasetType}`;
export const makeLocalSubflowPortOptionValue = (portId: string) => `local_subflow_port ${portId}`;

export const getDataInputOptionValue = (config: DataInputConfig): string => {
  try {
    if ('static_dataset_id' in config && config.static_dataset_id) {
      return makeStaticOptionValue(config.static_dataset_id);
    } else if ('linked_dataset_id' in config && config.linked_dataset_id) {
      return makeLinkedOptionValue(config.linked_dataset_id);
    } else if ('source_step_id' in config && config.source_step_id) {
      return makeDynamicOptionValue(config.source_step_id, config.output_id);
    } else if ('dataset_type' in config && config.dataset_type) {
      return makeEmptyOptionValue(config.dataset_type);
    } else if ('port_id' in config && config.port_id) {
      return makeLocalSubflowPortOptionValue(config.port_id);
    } else {
      return '';
    }
  } catch (error) {
    reportException(error);
    return '';
  }
};

export type Option = {
  value: string;
  label: string;
};

const EMPTY_DATASET_OPTIONS = [
  DatasetType.File,
  DatasetType.Dataframe,
  DatasetType.Json,
  DatasetType.Directory,
].map((type) => ({
  value: makeEmptyOptionValue(type),
  label: `Empty ${datasetTypeToString(type)} Dataset`,
}));

export const makeNewInputConfig = (value: string, options: Option[]): DataInput => {
  const lastKnownDisplayName = options.find((o) => o.value === value)?.label ?? null;
  if (value && value.startsWith('static')) {
    return {
      name: null,
      last_known_display_name: lastKnownDisplayName,
      data_input_type: DataInputType.Static,
      config: { static_dataset_id: value.replace('static ', '') },
    };
  } else if (value && value.startsWith('linked')) {
    return {
      name: null,
      last_known_display_name: lastKnownDisplayName,
      data_input_type: DataInputType.Linked,
      config: { linked_dataset_id: value.replace('linked ', '') },
    };
  } else if (value && value.startsWith('dynamic')) {
    const parts = value.split(' ');
    const outputId = parts.length > 2 ? parts[2] : null;
    const sourceStepId = parts[1];
    return {
      name: null,
      last_known_display_name: lastKnownDisplayName,
      data_input_type: DataInputType.Dynamic,
      config: { source_step_id: sourceStepId, output_id: outputId },
    };
  } else if (value && value.startsWith('empty')) {
    return {
      name: null,
      last_known_display_name: lastKnownDisplayName,
      data_input_type: DataInputType.Empty,
      config: { dataset_type: value.replace('empty ', '') as DatasetType },
    };
  } else if (value && value.startsWith('local_subflow_port')) {
    return {
      name: null,
      last_known_display_name: lastKnownDisplayName,
      data_input_type: DataInputType.LocalSubflowPort,
      config: { port_id: value.replace('local_subflow_port ', '') },
    };
  } else {
    throw new Error(`Unknown value for ${value}`);
  }
};

type DataInputScope = 'root' | 'local_subflow';

const useDataInputOptions = (
  stepId: string,
  includeEmptyDatasets: boolean = false,
  scopeOverride?: DataInputScope,
) => {
  const {
    data: staticDatasets,
    isPending: loadingStaticDatasets,
    isError: staticCatalogError,
  } = useRetrieveStaticDatasets();
  const {
    data: versionResources,
    isLoading: loadingVersionResources,
    isError: versionResourcesError,
  } = useStaticVersionResources();
  const staticError = staticCatalogError || versionResourcesError;
  const {
    data: linkedDatasets,
    isPending: loadingLinkedDatasets,
    isError: linkedError,
  } = useRetrieveLinkedDatasets();
  const unavailableSourceTypes = useMemo(
    () => [
      ...(staticError ? [DataInputType.Static] : []),
      ...(linkedError ? [DataInputType.Linked] : []),
    ],
    [staticError, linkedError],
  );
  const inventoryError = unavailableSourceTypes.length
    ? `${[...(staticError ? ['Static'] : []), ...(linkedError ? ['Linked'] : [])].join(' and ')} data sources could not be loaded. Refresh to try again.`
    : null;
  const { target } = useActiveCompositionTarget();
  const scope = scopeOverride ?? target.scope_kind;
  const { outputs: dynamicOptions, isPending: loadingDynamicDatasets } =
    useRetrieveUpstreamDataOutputs(stepId, scope);
  const { data: moduleContext, isPending: loadingModuleContext } =
    useRetrieveEditorLocalSubflowContext(
      scope === 'local_subflow' && target.scope_kind === 'local_subflow'
        ? target.subflow_id
        : undefined,
    );

  const { getIconForOptionValue, isLoading: loadingIcons } = useDataInputIconContext();

  const staticOptions = useMemo(() => {
    const selected = versionResources?.resources ?? [];
    const options = selected.map((resource) => ({
      value: makeStaticOptionValue(resource.source_id),
      label: resource.name,
    }));
    for (const source of staticDatasets?.results ?? []) {
      if (options.some((option) => option.value === makeStaticOptionValue(source.id))) continue;
      if (!versionResourcesError && source.latest_ready_revision_id)
        options.push({
          value: makeStaticOptionValue(source.id),
          label: source.available_dataset?.name ?? source.dataset.name,
        });
    }
    return options;
  }, [staticDatasets?.results, versionResources, versionResourcesError]);

  const linkedOptions = useMemo(() => {
    return (
      linkedDatasets?.results.map((linkedDataset) => ({
        value: makeLinkedOptionValue(linkedDataset.id),
        label: linkedDataset.name,
      })) || []
    );
  }, [linkedDatasets?.results]);

  const isLoading =
    loadingStaticDatasets ||
    loadingVersionResources ||
    loadingLinkedDatasets ||
    loadingDynamicDatasets ||
    (scope === 'local_subflow' && loadingModuleContext) ||
    loadingIcons;

  const localSubflowPortOptions = useMemo(() => {
    if (scope !== 'local_subflow') {
      return [];
    }
    return (moduleContext?.input_ports ?? []).map((port) => ({
      value: makeLocalSubflowPortOptionValue(port.port_id),
      label: `Module input: ${port.name || port.port_id} (${datasetTypeToString(port.dataset_type)})`,
    }));
  }, [moduleContext?.input_ports, scope]);

  const baseGroups = useMemo(() => {
    const groups = [
      { group: 'Static Datasets', items: staticOptions },
      { group: 'Linked Datasets', items: linkedOptions },
      { group: 'Dynamic Datasets', items: dynamicOptions },
    ];
    if (localSubflowPortOptions.length > 0) {
      groups.push({ group: 'Module Inputs', items: localSubflowPortOptions });
    }
    if (includeEmptyDatasets) {
      groups.push({ group: 'Empty Datasets', items: EMPTY_DATASET_OPTIONS });
    }
    return groups;
  }, [staticOptions, linkedOptions, dynamicOptions, includeEmptyDatasets, localSubflowPortOptions]);

  // Inject icons into all options
  const options = useMemo(() => {
    return withIconsForGroups(baseGroups, getIconForOptionValue);
  }, [baseGroups, getIconForOptionValue]);

  return { options, isLoading, inventoryError, unavailableSourceTypes };
};

export default useDataInputOptions;
